import {
	Building2,
	Check,
	CheckCircle2,
	ChevronDown,
	ChevronRight,
	Crown,
	Facebook,
	Globe,
	Instagram,
	Linkedin,
	Lock,
	Mail,
	MapPin,
	Pencil,
	Phone,
	Plus,
	Sparkles,
	Trash2,
	Twitter,
	Upload,
} from "lucide-react";
import React from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { Button } from "@/app/components/ui/button";
import { useUpdateClaimCompanyMutation } from "@/app/services/create-resume/queries";

import { BRAND_COLORS, TEMPLATE_OPTIONS } from "./constants";
import { useCompanyDashboard } from "./CompanyDashboardProvider";
import { getCompanyDashboardPath, getCompanyUpgradePath } from "./paths";
import { ContentSection } from "./shared";

function getSocialIcon(platform) {
	switch (platform) {
		case "LinkedIn":
			return <Linkedin className="h-4 w-4" />;
		case "Twitter":
			return <Twitter className="h-4 w-4" />;
		case "Facebook":
			return <Facebook className="h-4 w-4" />;
		case "Instagram":
			return <Instagram className="h-4 w-4" />;
		default:
			return <Globe className="h-4 w-4" />;
	}
}

const UNSUPPORTED_MEDIA_MESSAGE =
	"Logo and banner uploads are temporarily read-only until a company media upload backend is available.";
const UNSUPPORTED_STYLE_MESSAGE = "Template, color, and banner style changes are temporarily read-only.";
const UNSUPPORTED_SOCIAL_MESSAGE = "Social link edits are temporarily read-only in this dashboard.";

function normalizeText(value) {
	return typeof value === "string" ? value.trim() : "";
}

function ensureAbsoluteUrl(value) {
	const trimmed = normalizeText(value);
	if (!trimmed) return "";
	return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
}

function getErrorMessage(error, fallback) {
	return error?.response?.data?.message || fallback;
}

function toDetailsArray(values) {
	const list = Array.isArray(values) ? values : [];
	return list
		.map((item) => {
			if (typeof item === "string") {
				const value = normalizeText(item);
				return value ? { bp: true, value } : null;
			}
			if (item && typeof item === "object") {
				const value = normalizeText(item.value);
				if (!value) return null;
				const valueCategory = normalizeText(item.valueCategory);
				return {
					bp: Boolean(item.bp ?? true),
					value,
					valueCategory: valueCategory || undefined,
				};
			}
			return null;
		})
		.filter(Boolean);
}

function buildAboutSummary(aboutText, fallback = "") {
	const source = normalizeText(aboutText) || normalizeText(fallback);
	if (!source) return "";
	return source.slice(0, 500);
}

function buildCompanyUpdatePayload(company, branding, options = {}) {
	const { benefitsEdited = false } = options;
	const companyName = normalizeText(branding.companyName) || normalizeText(company?.company_name);
	const about = normalizeText(branding.about) || normalizeText(company?.about_us);
	const website = ensureAbsoluteUrl(branding.website) || normalizeText(company?.company_website);
	const linkedInUrl =
		branding.socialLinks?.find((link) => link?.platform === "LinkedIn")?.url || normalizeText(company?.linkedin_url);
	const headquarters = normalizeText(branding.address) || normalizeText(company?.headquarters);
	const locations = toDetailsArray(company?.locations);
	const benefits = benefitsEdited ? toDetailsArray(branding.whyWorkWithUs) : toDetailsArray(company?.benefits);

	const payload = {
		company_name: companyName,
		about_us: about,
		about_us_summary: buildAboutSummary(about, company?.about_us_summary),
		company_industry: normalizeText(branding.industry) || normalizeText(company?.company_industry),
		company_size_approx: normalizeText(company?.company_size_approx),
		headquarters,
		type: normalizeText(company?.type),
		founded: normalizeText(company?.founded),
		contactInformation: normalizeText(company?.contactInformation),
		specialties: normalizeText(company?.specialties),
		logo_url: normalizeText(company?.logo_url),
		company_website: website || undefined,
		linkedin_url: ensureAbsoluteUrl(linkedInUrl) || undefined,
		locations: locations.length ? locations : undefined,
		benefits: benefits.length ? benefits : undefined,
	};

	return payload;
}

function EditableSection({
	field,
	title,
	editingField,
	setEditingField,
	closeAllDropdowns,
	disabled = false,
	titleExtra,
	children,
	className = "",
}) {
	const isEditing = editingField === field;

	return (
		<div className={`relative ${className}`}>
			{title ? (
				<div className="mb-2 flex items-center justify-between">
					<div className="flex items-center gap-2">
						<h3 className="text-sm font-semibold text-foreground">{title}</h3>
						{titleExtra}
					</div>
					{!disabled && !isEditing ? (
						<button
							type="button"
							onClick={(event) => {
								event.stopPropagation();
								setEditingField(field);
								closeAllDropdowns();
							}}
							className="text-sm font-medium text-primary-700 hover:text-primary-800"
						>
							Edit
						</button>
					) : null}
				</div>
			) : null}
			{children}
		</div>
	);
}

function BrandingChecklistPanel({ items, completedCount, totalCount, progressPercentage, onNavigate }) {
	return (
		<div className="rounded-lg border border-border bg-white p-5">
			<div className="mb-5">
				<div className="mb-2 flex items-center justify-between">
					<h3 className="text-base font-semibold text-foreground">Complete Your Profile</h3>
					<span className="text-sm font-medium text-muted-foreground">
						{completedCount} of {totalCount} complete
					</span>
				</div>
				<div className="h-2 overflow-hidden rounded-full bg-secondary">
					<div
						className="company-dashboard-gradient h-full rounded-full transition-all duration-500"
						style={{ width: `${progressPercentage}%` }}
					/>
				</div>
				<p className="mt-2 text-xs text-muted-foreground">
					{progressPercentage}% complete. Finish the remaining items to unlock the full dashboard experience.
				</p>
			</div>

			<div className="space-y-2">
				{items.map((item) => (
					<button
						key={item.id}
						type="button"
						disabled={item.completed}
						onClick={() => !item.completed && onNavigate(item.targetTab, item.id)}
						className={`flex w-full items-center gap-3 rounded-xl p-3 text-left transition-colors ${
							item.completed ? "bg-green-50" : "hover:bg-secondary"
						}`}
					>
						<div
							className={`flex h-8 w-8 items-center justify-center rounded-full ${item.completed ? "bg-green-100" : "bg-gray-100"}`}
						>
							{item.completed ? (
								<span className="text-sm font-semibold text-green-700">✓</span>
							) : (
								<span className="text-sm font-semibold text-gray-500">•</span>
							)}
						</div>
						<div className="min-w-0 flex-1">
							<p className={`text-sm font-medium ${item.completed ? "text-green-700" : "text-foreground"}`}>
								{item.label}
							</p>
							<p className="text-xs text-muted-foreground">{item.description}</p>
						</div>
						{!item.completed ? <ChevronRight className="h-4 w-4 text-muted-foreground" /> : null}
					</button>
				))}
			</div>
		</div>
	);
}

export default function BrandingTab() {
	const navigate = useNavigate();
	const {
		branding,
		plan,
		company,
		companyId,
		checklist,
		updateBranding,
		updateSocialLinks,
		updateBenefits,
		clearBrandingDraft,
	} = useCompanyDashboard();
	const [editingField, setEditingField] = React.useState(null);
	const [showTemplatePicker, setShowTemplatePicker] = React.useState(false);
	const [showColorPicker, setShowColorPicker] = React.useState(false);
	const [showBannerStylePicker, setShowBannerStylePicker] = React.useState(false);
	const [hasChanges, setHasChanges] = React.useState(false);
	const [hasBenefitChanges, setHasBenefitChanges] = React.useState(false);
	const logoInputRef = React.useRef(null);
	const bannerInputRef = React.useRef(null);
	const previewRef = React.useRef(null);
	const updateCompanyMutation = useUpdateClaimCompanyMutation(companyId);

	const canEditMedia = false;
	const canEditSocialLinks = false;
	const canCustomizePresentation = false;
	const canEditBanner = plan !== "starter" && canEditMedia;
	const canEditBenefits = plan !== "starter";
	const isSaving = updateCompanyMutation.isPending;

	const markChanged = React.useCallback(() => {
		setHasChanges(true);
	}, []);

	const handleFileUpload = (event) => {
		event.target.value = "";
		toast.info(UNSUPPORTED_MEDIA_MESSAGE);
	};

	const notifyUnsupportedMedia = React.useCallback(() => {
		toast.info(UNSUPPORTED_MEDIA_MESSAGE);
	}, []);

	const notifyUnsupportedStyle = React.useCallback(() => {
		toast.info(UNSUPPORTED_STYLE_MESSAGE);
	}, []);

	const notifyUnsupportedSocial = React.useCallback(() => {
		toast.info(UNSUPPORTED_SOCIAL_MESSAGE);
	}, []);

	React.useEffect(() => {
		setHasChanges(false);
		setHasBenefitChanges(false);
		setEditingField(null);
	}, [companyId]);

	const save = async () => {
		if (!company) {
			toast.error("Company data is not loaded yet. Refresh and try again.");
			return;
		}

		const payload = buildCompanyUpdatePayload(company, branding, { benefitsEdited: hasBenefitChanges });
		if (!payload.company_name || !payload.about_us || !payload.company_industry || !payload.headquarters) {
			toast.error("Company profile is missing required fields. Complete the required sections and try again.");
			return;
		}

		try {
			await updateCompanyMutation.mutateAsync(payload);
			clearBrandingDraft();
			setHasChanges(false);
			setEditingField(null);
			toast.success("Company profile updated successfully.");
		} catch (error) {
			toast.error(getErrorMessage(error, "Failed to save company profile. Please try again."));
		}
	};

	const updateLink = (index, field, value) => {
		updateSocialLinks(
			branding.socialLinks.map((item, itemIndex) => (itemIndex === index ? { ...item, [field]: value } : item)),
		);
		markChanged();
	};

	const removeLink = (index) => {
		updateSocialLinks(branding.socialLinks.filter((_, itemIndex) => itemIndex !== index));
		markChanged();
	};

	const addLink = () => {
		updateSocialLinks([...branding.socialLinks, { platform: "Website", url: "" }]);
		markChanged();
	};

	const updateBenefit = (index, value) => {
		updateBenefits(branding.whyWorkWithUs.map((item, itemIndex) => (itemIndex === index ? value : item)));
		setHasBenefitChanges(true);
		markChanged();
	};

	const removeBenefit = (index) => {
		updateBenefits(branding.whyWorkWithUs.filter((_, itemIndex) => itemIndex !== index));
		setHasBenefitChanges(true);
		markChanged();
	};

	const closeAllDropdowns = React.useCallback(() => {
		setShowTemplatePicker(false);
		setShowColorPicker(false);
		setShowBannerStylePicker(false);
	}, []);

	const handleChecklistNavigate = React.useCallback(
		(tabId, sectionId) => {
			if (tabId === "branding" && sectionId) {
				const element = document.getElementById(sectionId);
				if (element) {
					element.scrollIntoView({ behavior: "smooth", block: "center" });
				}
				return;
			}

			navigate(getCompanyDashboardPath(tabId, sectionId));
		},
		[navigate],
	);

	return (
		<ContentSection companyHeader>
			<div className="flex h-full flex-col">
				<div className="sticky top-0 z-20 flex items-center justify-between border-b border-border bg-white px-4 py-3">
					<div className="flex items-center gap-3">
						<div className="relative">
							<button
								type="button"
								onClick={() => {
									if (!canCustomizePresentation) {
										notifyUnsupportedStyle();
										return;
									}
									setShowTemplatePicker((value) => !value);
									setShowColorPicker(false);
									setShowBannerStylePicker(false);
								}}
								className={`flex items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm font-medium ${
									canCustomizePresentation ? "hover:bg-secondary" : "cursor-not-allowed opacity-60"
								}`}
							>
								<span>
									Template: {TEMPLATE_OPTIONS.find((template) => template.id === branding.selectedTemplate)?.name}
								</span>
								<ChevronDown className="h-4 w-4" />
							</button>
							{showTemplatePicker && canCustomizePresentation ? (
								<div className="absolute left-0 top-full z-30 mt-1 w-64 rounded-lg border border-border bg-white p-2 shadow-lg">
									{TEMPLATE_OPTIONS.map((template) => (
										<button
											key={template.id}
											type="button"
											onClick={() => {
												updateBranding({ selectedTemplate: template.id });
												markChanged();
												setShowTemplatePicker(false);
											}}
											className={`flex w-full items-center gap-3 rounded-md p-2 text-left hover:bg-secondary ${
												branding.selectedTemplate === template.id ? "bg-blue-50" : ""
											}`}
										>
											<div className="flex h-8 w-10 items-center justify-center rounded bg-gray-800 p-1">
												<div className="h-full w-full rounded border border-gray-600 bg-gray-700" />
											</div>
											<div className="flex-1">
												<p className="text-sm font-medium text-foreground">{template.name}</p>
												<p className="text-xs text-muted-foreground">{template.description}</p>
											</div>
											{branding.selectedTemplate === template.id ? (
												<Check className="h-4 w-4 text-primary-700" />
											) : null}
										</button>
									))}
								</div>
							) : null}
						</div>

						<div className="relative">
							<button
								type="button"
								onClick={() => {
									if (!canCustomizePresentation) {
										notifyUnsupportedStyle();
										return;
									}
									setShowColorPicker((value) => !value);
									setShowTemplatePicker(false);
									setShowBannerStylePicker(false);
								}}
								className={`flex items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm font-medium ${
									canCustomizePresentation ? "hover:bg-secondary" : "cursor-not-allowed opacity-60"
								}`}
							>
								<div
									className="h-5 w-5 rounded-full border border-border"
									style={{ backgroundColor: branding.primaryColor }}
								/>
								<span>Color</span>
								<ChevronDown className="h-4 w-4" />
							</button>
							{showColorPicker && canCustomizePresentation ? (
								<div className="absolute left-0 top-full z-30 mt-1 w-72 rounded-lg border border-border bg-white p-3 shadow-lg">
									<p className="mb-2 text-xs font-medium text-muted-foreground">Brand Color</p>
									<div className="mb-3 flex flex-wrap gap-2">
										{BRAND_COLORS.map((color) => (
											<button
												key={color}
												type="button"
												onClick={() => {
													updateBranding({ primaryColor: color });
													markChanged();
												}}
												className={`h-8 w-8 rounded-full transition-all ${
													branding.primaryColor === color
														? "scale-110 ring-2 ring-primary-500 ring-offset-2"
														: "hover:scale-110"
												}`}
												style={{ backgroundColor: color }}
											/>
										))}
									</div>
									<div className="flex items-center gap-2 border-t border-border pt-2">
										<input
											type="color"
											value={branding.primaryColor}
											onChange={(event) => {
												updateBranding({ primaryColor: event.target.value });
												markChanged();
											}}
											className="h-8 w-8 cursor-pointer rounded border-0 p-0"
										/>
										<input
											type="text"
											value={branding.primaryColor.toUpperCase()}
											onChange={(event) => {
												const value = event.target.value;
												if (/^#[0-9A-Fa-f]{0,6}$/.test(value)) {
													updateBranding({ primaryColor: value });
													markChanged();
												}
											}}
											className="flex-1 rounded-md border border-border px-3 py-1.5 text-sm font-mono"
											placeholder="#000000"
										/>
									</div>
								</div>
							) : null}
						</div>

						<div className="relative">
							<button
								type="button"
								onClick={() => {
									if (!canCustomizePresentation || !canEditBanner) {
										notifyUnsupportedStyle();
										return;
									}
									setShowBannerStylePicker((value) => !value);
									setShowTemplatePicker(false);
									setShowColorPicker(false);
								}}
								className={`flex items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm font-medium ${
									canEditBanner && canCustomizePresentation ? "hover:bg-secondary" : "cursor-not-allowed opacity-60"
								}`}
							>
								<span>
									Banner:{" "}
									{branding.bannerStyle === "default"
										? "Default"
										: branding.bannerStyle === "minimal"
											? "Simplified"
											: "Custom"}
								</span>
								{!canEditBanner ? <Lock className="h-3 w-3" /> : <ChevronDown className="h-4 w-4" />}
							</button>
							{showBannerStylePicker && canEditBanner && canCustomizePresentation ? (
								<div className="absolute left-0 top-full z-30 mt-1 w-64 rounded-lg border border-border bg-white p-2 shadow-lg">
									<button
										type="button"
										onClick={() => {
											updateBranding({ bannerStyle: "default" });
											markChanged();
											setShowBannerStylePicker(false);
										}}
										className={`flex w-full items-center gap-3 rounded-md p-2 text-left hover:bg-secondary ${
											branding.bannerStyle === "default" ? "bg-blue-50" : ""
										}`}
									>
										<div className="h-8 w-12 rounded bg-gradient-to-r from-blue-400 to-blue-600" />
										<div className="flex-1">
											<p className="text-sm font-medium text-foreground">Default</p>
											<p className="text-xs text-muted-foreground">Solid brand color</p>
										</div>
										{branding.bannerStyle === "default" ? <Check className="h-4 w-4 text-primary-700" /> : null}
									</button>
									<button
										type="button"
										onClick={() => {
											updateBranding({ bannerStyle: "minimal" });
											markChanged();
											setShowBannerStylePicker(false);
										}}
										className={`flex w-full items-center gap-3 rounded-md p-2 text-left hover:bg-secondary ${
											branding.bannerStyle === "minimal" ? "bg-blue-50" : ""
										}`}
									>
										<div className="h-8 w-12 rounded border border-border bg-secondary" />
										<div className="flex-1">
											<p className="text-sm font-medium text-foreground">Simplified</p>
											<p className="text-xs text-muted-foreground">Minimal style</p>
										</div>
										{branding.bannerStyle === "minimal" ? <Check className="h-4 w-4 text-primary-700" /> : null}
									</button>
									<button
										type="button"
										onClick={() => {
											bannerInputRef.current?.click();
											setShowBannerStylePicker(false);
										}}
										className={`flex w-full items-center gap-3 rounded-md p-2 text-left hover:bg-secondary ${
											branding.bannerStyle === "custom" ? "bg-blue-50" : ""
										}`}
									>
										<div className="flex h-8 w-12 items-center justify-center rounded border border-dashed border-border bg-secondary">
											<Upload className="h-3 w-3 text-muted-foreground" />
										</div>
										<div className="flex-1">
											<div className="flex items-center gap-1.5">
												<p className="text-sm font-medium text-foreground">Custom Image</p>
												<Crown className="h-3.5 w-3.5 text-amber-500" />
											</div>
											<p className="text-xs text-muted-foreground">Upload your own</p>
										</div>
										{branding.bannerStyle === "custom" ? <Check className="h-4 w-4 text-primary-700" /> : null}
									</button>
								</div>
							) : null}
						</div>
					</div>

					{hasChanges ? (
						<Button onClick={save} disabled={isSaving}>
							{isSaving ? "Saving..." : "Save Changes"}
						</Button>
					) : null}
				</div>
				<div className="border-b border-border bg-primary-50 px-4 py-2 text-xs text-primary-900">
					Style customization, media uploads, and social link edits are read-only for now. This save action syncs
					company details supported by the backend.
				</div>

				<div className="flex flex-1 overflow-hidden">
					<div
						className="flex-1 overflow-auto p-6"
						onClick={() => {
							if (editingField) setEditingField(null);
							closeAllDropdowns();
						}}
					>
						<div
							ref={previewRef}
							className="mx-auto max-w-2xl overflow-hidden rounded-lg border border-border bg-white shadow-sm"
							onClick={(event) => event.stopPropagation()}
						>
							<div id="customize_banner">
								<div
									className={`relative h-60 w-full ${canEditBanner ? "group cursor-pointer" : "cursor-not-allowed"} ${
										branding.bannerStyle === "default"
											? "bg-gradient-to-r from-blue-400 to-blue-600"
											: branding.bannerStyle === "minimal"
												? "bg-secondary"
												: "bg-gray-100"
									}`}
									style={
										branding.bannerStyle === "default"
											? {
													background: `linear-gradient(to right, ${branding.primaryColor}99, ${branding.primaryColor})`,
												}
											: undefined
									}
									onClick={() => {
										if (canEditBanner) {
											bannerInputRef.current?.click();
											return;
										}
										notifyUnsupportedMedia();
									}}
								>
									{branding.bannerStyle === "custom" && branding.companyBanner ? (
										<img src={branding.companyBanner} alt="Company banner" className="h-full w-full object-cover" />
									) : null}
									{canEditBanner ? (
										<div className="absolute inset-0 flex items-center justify-center bg-black/0 opacity-0 transition-colors group-hover:bg-black/10 group-hover:opacity-100">
											<div className="rounded-full bg-white p-3 shadow-lg">
												<Upload className="h-5 w-5 text-gray-600" />
											</div>
										</div>
									) : (
										<div className="absolute top-4 right-4">
											<div className="bg-white/90 px-3 py-1.5 rounded-full flex items-center gap-1.5 text-sm">
												<Lock className="w-3.5 h-3.5" style={{ color: "#d97706" }} />
												<span className="font-medium" style={{ color: "#d97706" }}>
													Read-only
												</span>
											</div>
										</div>
									)}
								</div>
								<input
									ref={bannerInputRef}
									type="file"
									accept="image/*"
									onChange={handleFileUpload}
									className="hidden"
								/>
							</div>

							<div className="relative z-10 -mt-12 px-6 pb-6">
								<div className="flex items-end gap-4">
									<div id="company_logo" className="group relative shrink-0">
										<div
											className={`relative flex h-32 w-32 cursor-pointer items-center justify-center rounded-full ring-4 ring-white shadow-lg ${
												branding.companyLogo
													? "overflow-hidden bg-white"
													: "bg-gradient-to-br from-primary-500 to-primary-700"
											}`}
											onClick={() => {
												notifyUnsupportedMedia();
												closeAllDropdowns();
											}}
										>
											{branding.companyLogo ? (
												<img
													src={branding.companyLogo}
													alt={`${branding.companyName} logo`}
													className="h-full w-full object-cover"
												/>
											) : (
												<Building2 className="h-10 w-10 text-white" />
											)}
											{editingField !== "logo" ? (
												<div className="absolute inset-0 flex items-center justify-center rounded-full bg-black/0 opacity-0 transition-colors group-hover:bg-black/20 group-hover:opacity-100">
													<div className="rounded-full bg-white p-2 shadow-lg">
														<Pencil className="h-4 w-4 text-gray-600" />
													</div>
												</div>
											) : null}
										</div>
										{editingField === "logo" ? (
											<div className="absolute -bottom-2 left-1/2 z-10 flex -translate-x-1/2 gap-1">
												<button
													type="button"
													onClick={(event) => {
														event.stopPropagation();
														logoInputRef.current?.click();
													}}
													className="rounded-full bg-white p-1.5 shadow-lg hover:bg-gray-50"
												>
													<Upload className="h-4 w-4 text-gray-600" />
												</button>
												{branding.companyLogo ? (
													<button
														type="button"
														onClick={(event) => {
															event.stopPropagation();
															updateBranding({ companyLogo: null });
															markChanged();
														}}
														className="rounded-full bg-white p-1.5 shadow-lg hover:bg-red-50"
													>
														<Trash2 className="h-4 w-4 text-red-500" />
													</button>
												) : null}
											</div>
										) : null}
										<input
											ref={logoInputRef}
											type="file"
											accept="image/*"
											onChange={handleFileUpload}
											className="hidden"
										/>
									</div>

									<EditableSection
										field="companyName"
										editingField={editingField}
										setEditingField={setEditingField}
										closeAllDropdowns={closeAllDropdowns}
										className="flex-1 pb-2"
									>
										{editingField === "companyName" ? (
											<div className="space-y-2" onClick={(event) => event.stopPropagation()}>
												<input
													type="text"
													value={branding.companyName}
													onChange={(event) => {
														updateBranding({ companyName: event.target.value });
														markChanged();
													}}
													className="w-full rounded-md border border-primary-300 px-2 py-1 text-xl font-semibold focus:outline-none focus:ring-2 focus:ring-primary-500"
													autoFocus
												/>
												<input
													type="text"
													value={branding.industry}
													onChange={(event) => {
														updateBranding({ industry: event.target.value });
														markChanged();
													}}
													className="w-full rounded-md border border-primary-300 px-2 py-1 text-sm text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary-500"
													placeholder="Industry"
												/>
											</div>
										) : (
											<div>
												<h1 className="text-xl font-semibold text-foreground">{branding.companyName}</h1>
												<p className="text-sm text-muted-foreground">{branding.industry}</p>
											</div>
										)}
									</EditableSection>
								</div>
							</div>

							<div className="space-y-6 p-6 pt-6">
								<EditableSection
									field="about"
									title="About"
									editingField={editingField}
									setEditingField={setEditingField}
									closeAllDropdowns={closeAllDropdowns}
								>
									<div id="company_description" className="rounded-lg">
										{editingField === "about" ? (
											<div onClick={(event) => event.stopPropagation()}>
												<textarea
													value={branding.about}
													onChange={(event) => {
														updateBranding({ about: event.target.value });
														markChanged();
													}}
													rows={5}
													className="w-full resize-none rounded-md border border-primary-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
													autoFocus
												/>
												<div className="mt-1 flex justify-end">
													<span
														className={`text-xs ${branding.about.length > 900 ? "text-amber-600" : "text-muted-foreground"}`}
													>
														{branding.about.length}/1000
													</span>
												</div>
											</div>
										) : (
											<p className="whitespace-pre-wrap text-sm text-muted-foreground">
												{branding.about || <span className="italic">Click to add a company description...</span>}
											</p>
										)}
									</div>
								</EditableSection>

								<EditableSection
									field="contact"
									title="Contact Information"
									editingField={editingField}
									setEditingField={setEditingField}
									closeAllDropdowns={closeAllDropdowns}
								>
									<div id="contact_info" className="rounded-lg">
										{editingField === "contact" ? (
											<div
												className="grid grid-cols-1 gap-3 md:grid-cols-2"
												onClick={(event) => event.stopPropagation()}
											>
												<div>
													<label className="mb-1 block text-xs text-muted-foreground">Website</label>
													<input
														type="url"
														value={branding.website}
														onChange={(event) => {
															updateBranding({ website: event.target.value });
															markChanged();
														}}
														placeholder="https://..."
														className="w-full rounded-md border border-primary-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
													/>
												</div>
												<div>
													<label className="mb-1 block text-xs text-muted-foreground">Email</label>
													<input
														type="email"
														value={branding.email}
														onChange={() => {}}
														placeholder="careers@..."
														disabled
														className="w-full cursor-not-allowed rounded-md border border-border bg-secondary px-3 py-2 text-sm text-muted-foreground"
													/>
												</div>
												<div>
													<label className="mb-1 block text-xs text-muted-foreground">Phone</label>
													<input
														type="tel"
														value={branding.phone}
														onChange={() => {}}
														placeholder="+255..."
														disabled
														className="w-full cursor-not-allowed rounded-md border border-border bg-secondary px-3 py-2 text-sm text-muted-foreground"
													/>
												</div>
												<div>
													<label className="mb-1 block text-xs text-muted-foreground">Address</label>
													<input
														type="text"
														value={branding.address}
														onChange={(event) => {
															updateBranding({ address: event.target.value });
															markChanged();
														}}
														placeholder="Office address"
														className="w-full rounded-md border border-primary-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
													/>
												</div>
												<p className="md:col-span-2 text-xs text-muted-foreground">
													Email and phone fields are currently read-only in this dashboard.
												</p>
											</div>
										) : (
											<div className="grid grid-cols-1 gap-2 md:grid-cols-2">
												{branding.website ? (
													<div className="flex items-center gap-2 text-sm">
														<Globe className="h-4 w-4 text-muted-foreground" />
														<span className="text-primary-700">{branding.website}</span>
													</div>
												) : null}
												{branding.email ? (
													<div className="flex items-center gap-2 text-sm">
														<Mail className="h-4 w-4 text-muted-foreground" />
														<span className="text-primary-700">{branding.email}</span>
													</div>
												) : null}
												{branding.phone ? (
													<div className="flex items-center gap-2 text-sm text-muted-foreground">
														<Phone className="h-4 w-4" />
														<span>{branding.phone}</span>
													</div>
												) : null}
												{branding.address ? (
													<div className="flex items-center gap-2 text-sm text-muted-foreground">
														<MapPin className="h-4 w-4" />
														<span>{branding.address}</span>
													</div>
												) : null}
												{!branding.website && !branding.email && !branding.phone && !branding.address ? (
													<p className="text-sm italic text-muted-foreground">Click to add contact details...</p>
												) : null}
											</div>
										)}
									</div>
								</EditableSection>

								<EditableSection
									field="social"
									title="Social Links"
									editingField={editingField}
									setEditingField={setEditingField}
									closeAllDropdowns={closeAllDropdowns}
									disabled={!canEditSocialLinks}
									titleExtra={
										!canEditSocialLinks ? (
											<button
												type="button"
												onClick={notifyUnsupportedSocial}
												className="rounded-md border border-border px-2 py-0.5 text-xs font-medium text-muted-foreground hover:bg-secondary"
											>
												Read-only
											</button>
										) : undefined
									}
								>
									<div id="social_links" className="rounded-lg">
										{editingField === "social" ? (
											<div className="space-y-2" onClick={(event) => event.stopPropagation()}>
												{branding.socialLinks.map((link, index) => (
													<div key={`${link.platform}-${index}`} className="flex items-center gap-2">
														<div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-secondary text-muted-foreground">
															{getSocialIcon(link.platform)}
														</div>
														<select
															value={link.platform}
															onChange={(event) => updateLink(index, "platform", event.target.value)}
															className="w-28 rounded-md border border-primary-300 px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
														>
															<option value="LinkedIn">LinkedIn</option>
															<option value="Twitter">Twitter</option>
															<option value="Facebook">Facebook</option>
															<option value="Instagram">Instagram</option>
															<option value="Website">Website</option>
														</select>
														<input
															type="url"
															value={link.url}
															onChange={(event) => updateLink(index, "url", event.target.value)}
															placeholder="https://..."
															className="flex-1 rounded-md border border-primary-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
														/>
														<button
															type="button"
															onClick={() => removeLink(index)}
															className="p-2 text-muted-foreground hover:text-red-500"
														>
															<Trash2 className="h-4 w-4" />
														</button>
													</div>
												))}
												<Button variant="outline" size="sm" onClick={addLink} className="mt-2">
													<Plus className="h-4 w-4" />
													Add Link
												</Button>
											</div>
										) : branding.socialLinks.length ? (
											<div className="flex flex-wrap gap-2">
												{branding.socialLinks.map((link, index) => (
													<div
														key={`${link.platform}-${index}`}
														className="flex items-center gap-2 rounded-lg bg-secondary px-3 py-2"
													>
														<span className="text-muted-foreground">{getSocialIcon(link.platform)}</span>
														<span className="text-sm font-medium">{link.platform}</span>
													</div>
												))}
											</div>
										) : (
											<p className="text-sm italic text-muted-foreground">Click to add social links...</p>
										)}
									</div>
								</EditableSection>

								<EditableSection
									field="benefits"
									title="Why Work With Us"
									editingField={editingField}
									setEditingField={setEditingField}
									closeAllDropdowns={closeAllDropdowns}
									disabled={!canEditBenefits}
									titleExtra={
										!canEditBenefits ? (
											<span
												className="flex items-center gap-1 px-2 py-1 rounded-md bg-[#ffecc7] text-[#985508] text-xs font-medium"
												style={{ backgroundColor: "#ffecc7", color: "#985508" }}
											>
												<Lock className="w-3 h-3" /> Corporate
											</span>
										) : undefined
									}
								>
									<div id="why_work_here" className="rounded-lg border border-secondary bg-secondary/30 p-4">
										{canEditBenefits ? (
											editingField === "benefits" ? (
												<div className="space-y-2" onClick={(event) => event.stopPropagation()}>
													{branding.whyWorkWithUs.map((benefit, index) => (
														<div key={`benefit-${index}`} className="flex items-center gap-2">
															<CheckCircle2 className="h-5 w-5 shrink-0 text-green-600" />
															<input
																type="text"
																value={benefit}
																onChange={(event) => updateBenefit(index, event.target.value)}
																placeholder="Add a benefit..."
																className="flex-1 rounded-md border border-primary-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
															/>
															<button
																type="button"
																onClick={() => removeBenefit(index)}
																className="p-2 text-muted-foreground hover:text-red-500"
															>
																<Trash2 className="h-4 w-4" />
															</button>
														</div>
													))}
													<Button
														variant="outline"
														size="sm"
														onClick={() => {
															updateBenefits([...branding.whyWorkWithUs, ""]);
															setHasBenefitChanges(true);
															markChanged();
														}}
														className="mt-2"
													>
														<Plus className="h-4 w-4" />
														Add Benefit
													</Button>
												</div>
											) : branding.whyWorkWithUs.filter(Boolean).length ? (
												<ul className="space-y-2">
													{branding.whyWorkWithUs.filter(Boolean).map((benefit, index) => (
														<li key={`preview-benefit-${index}`} className="flex items-center gap-2">
															<CheckCircle2 className="h-4 w-4 shrink-0 text-green-600" />
															<span className="text-sm text-muted-foreground">{benefit}</span>
														</li>
													))}
												</ul>
											) : (
												<p className="text-sm italic text-muted-foreground">Click to add benefits...</p>
											)
										) : (
											<div className="py-4 text-center">
												<Crown className="mx-auto mb-2 h-8 w-8 text-amber-500" />
												<p className="mb-3 text-sm text-muted-foreground">
													Upgrade to highlight what makes your company great
												</p>
												<div className="flex justify-center">
													<Button size="sm" variant="outline" onClick={() => navigate(getCompanyUpgradePath())}>
														<Sparkles className="h-3.5 w-3.5" />
														Upgrade
													</Button>
												</div>
											</div>
										)}
									</div>
								</EditableSection>
							</div>
						</div>

						<p className="mt-4 text-center text-xs text-muted-foreground">
							Click any element to edit it directly. Changes will be saved when you click Save Changes.
						</p>
					</div>

					{!checklist.isSetupComplete ? (
						<div className="hidden w-[28rem] overflow-auto border-l border-border bg-white p-4 xl:block">
							<BrandingChecklistPanel
								items={checklist.items}
								completedCount={checklist.completedCount}
								totalCount={checklist.totalCount}
								progressPercentage={checklist.progressPercentage}
								onNavigate={handleChecklistNavigate}
							/>
						</div>
					) : null}
				</div>
			</div>
		</ContentSection>
	);
}
